import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { faChalkboardUser, faInbox } from '@fortawesome/free-solid-svg-icons';
import { FileUploader, FileUploadModule } from 'ng2-file-upload';
import { catchError, forkJoin, of } from 'rxjs';
import { ItemAccessComponent } from 'src/app/controls/item-access/item-access.component';
import { ItemAccess } from 'src/app/controls/item-access/models/item-access';
import { ReachableElement } from 'src/app/controls/item-access/models/reachable-element';
import { Group } from 'src/app/models/group';
import { User } from 'src/app/models/user';
import { View } from 'src/app/models/view';
import { ViewAccessibility } from 'src/app/models/view-accessibility';
import { DefaultHttpService } from 'src/app/services/default-http.service';
import { GroupsService } from 'src/app/services/groups.service';
import { MemoryService } from 'src/app/services/memory.service';
import { ViewsService } from 'src/app/services/views.service';

@Component({
    selector: 'app-group-views-accessibility',
    imports: [ItemAccessComponent, MatButtonModule, FileUploadModule],
    templateUrl: './group-views-accessibility.component.html',
    styleUrl: './group-views-accessibility.component.scss'
})
export class GroupViewsAccessibilityComponent extends DefaultHttpService implements OnInit {
    public groupsService = inject(GroupsService);
    public viewsService = inject(ViewsService);

    public faViews = faChalkboardUser;
    public faInbox = faInbox;

    _group: Group;

    @Input()
    public set group(value: Group) {
        this.loading = true;
        this._group = value;
        this.reachableElement = new ReachableElement(this.group.id, this.group.displayName, faInbox);

        this.loadInformations();
    }

    public get group(): Group {
        return this._group;
    }

    /**
     * Valeur indiquant si le formulaire est disabled.
     */
    @Input()
    public disabled: boolean;

    @Output()
    public viewsAccessibilitiesUpdated: EventEmitter<ViewAccessibility[]> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    private viewsAccessibilitiesToSave: ViewAccessibility[];

    allViewsAccess: ItemAccess<View>[];

    availableViewsAccess: ItemAccess<View>[];

    selectedViewsAccess: ItemAccess<View>[];

    originalViewsAccess: ItemAccess<View>[];

    inheritedViewsAccess: ItemAccess<View>[];

    reachableElement: ReachableElement = null;

    /**
     * Valeur indiquant que les données sont en cours de chargement.
     */
    public loading = true;

    // Accès au enfant de type fileToUpload
    @ViewChild('fileToUpload', { static: true }) fileToUploadVariable: ElementRef;

    // Composant FileUploaderCustom
    public uploader: FileUploader = null;

    constructor(public override memoryService: MemoryService) {
        super(memoryService);
    }

    ngOnInit(): void {
        this.uploader = new FileUploader({
            url: `${this.baseUrl}/api/organizations/${this.organizationId}/groups/${this.group.id}/viewsAccessibilities/import`,
            authTokenHeader: 'Authorization',
            authToken: `Bearer ${this.connectedUser.token.accessToken}`,
            allowedFileType: ['xls'],
            queueLimit: 1,
        });

        this.uploader.onAfterAddingFile = (fileItem) => {
            fileItem.withCredentials = false;
        };

        this.uploader.onCompleteAll = () => {
            this.loadInformations();
        };
    }

    public onAccessUpdated(itemAccessList: ItemAccess<View>[]) {
        // Ne garde que les éléments non hérités.
        itemAccessList = itemAccessList.filter((x) => x.inherited === false);
        console.log('itemAccessList', itemAccessList);
        this.viewsAccessibilitiesToSave = itemAccessList.map((x) => {
            let viewAccessibility = new ViewAccessibility();
            viewAccessibility.id = x.accessId;
            viewAccessibility.viewId = x.item.id;
            viewAccessibility.userId = this.group.id;
            return viewAccessibility;
        });
    }

    public cancel(): void {
        this.canceled.emit(true);
    }

    public save(): void {
        this.viewsAccessibilitiesUpdated.emit(this.viewsAccessibilitiesToSave);
    }

    public onExported() {
        this.groupsService
            .exportViews(this._group.id)
            .pipe(
                catchError((err) => {
                    console.error('Export Error');
                    return of(null);
                })
            )
            .subscribe((blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = `Vues accessibles à l\`utilisateur ${this._group.id}.xlsx`;
                a.click();
                URL.revokeObjectURL(objectUrl);
            });
    }

    public onFileSelected() {
        this.uploader.uploadAll();
    }

    /**
     * Determine les vues qui ne sont pas accessible par l'utilisateur.
     */
    private determinedAvailableViewsAccess() {
        this.availableViewsAccess = [...this.allViewsAccess];
        // Ne garde que les vues pour lesquelles il n'existe pas d'accès.
        this.availableViewsAccess = this.availableViewsAccess.filter((a) => this.selectedViewsAccess.some((z) => z.item.id === a.item.id) === false);
    }

    private sortSelectedViewsAccess() {
        this.selectedViewsAccess = this.selectedViewsAccess.sort((x, y) => x.item.displayName.localeCompare(y.item.displayName));
    }

    private sortAvailableViewsAccess() {
        this.availableViewsAccess = this.availableViewsAccess.sort((x, y) => x.item.displayName.localeCompare(y.item.displayName));
    }

    private loadInformations(): void {
        forkJoin([this.groupsService.getViews(this.group.id), this.groupsService.getInheritedViews(this.group.id), this.viewsService.List(null, null, null, null)]).subscribe(
            (x) => {
                this.allViewsAccess = x[2].value.map((box) => {
                    let access = new ItemAccess<View>(box, false, false);
                    return access;
                });

                this.inheritedViewsAccess = x[1].map((z) => {
                    let viewAccess = this.allViewsAccess.find((y) => y.item.id === z.viewId).Clone();
                    viewAccess.inherited = z.isInherited;
                    return viewAccess;
                });

                this.originalViewsAccess = x[0].map((z) => {
                    let viewAccess = this.allViewsAccess.find((y) => y.item.id === z.viewId).Clone();
                    viewAccess.inherited = z.isInherited;
                    viewAccess.accessId = z.id;
                    return viewAccess;
                });

                this.selectedViewsAccess = [...this.originalViewsAccess];

                // Ajoute les accès hérités aux vues s'ils ne sont pas déjà présents.
                this.selectedViewsAccess = this.selectedViewsAccess.concat(
                    this.inheritedViewsAccess.filter((y) => this.selectedViewsAccess.some((z) => z.item.id === y.item.id) === false)
                );

                this.determinedAvailableViewsAccess();
                this.sortSelectedViewsAccess();
                this.sortAvailableViewsAccess();
                this.loading = false;
            }
        );
    }
}
